import { useMemo } from 'react'
import { IconButton, styled, Typography } from '@mui/material'
import { UserAvatar } from '$common/avatar'
import {
  useCallStatusText,
  usePvCallOpponentInfo,
} from '$common/react-hooks/call'
import { closePvCallManager } from '$logic/router'
import { formatCallDuration, useCallStorage } from '$logic/call'
import { ArrowRightIcon } from '$common/icons'
import { AvatarSize } from '$common/avatar/avatar-base/types'
import {
  EndedCallControls,
  IncomingCallControls,
  OutgoingCallControls,
} from './components'

const Root = styled('div')`
  position: relative;
  width: 100%;
  height: 100%;
  background: linear-gradient(180deg, #998cd9 0%, #5540bf 100%);
`

const BackgroundImage = styled('div')<{ src: string }>`
  width: 100%;
  height: 100%;

  background-image: ${({ src }) => `url('${src}')`};
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;

  transform: scale(1.2);
  filter: blur(5px);
`

const BlackOverlay = styled('div')`
  width: 100%;
  height: 100%;
  position: absolute;
  background-color: #09203a;

  opacity: 0.75;
  top: 0;
  left: 0;
`

const BackButton = styled(IconButton)`
  position: absolute;
  top: 8px;
  right: 8px;
  width: 48px;
  height: 48px;
`

const OpponentInfo = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: center;

  width: 100%;
  position: absolute;
  top: 136px;
`

const CallDuration = styled(Typography)`
  /* @noflip */
  direction: ltr;

  padding: 4px 16px;
  background-color: #00000033;
  border-radius: 16px;
  margin-top: 8px;
  color: #a7a7a7;
`

export function PvCallManagerPage() {
  const { state, status, duration } = useCallStorage()

  const {
    id: opponentId,
    image: opponentImage,
    name: opponentName,
  } = usePvCallOpponentInfo()

  const statusText = useCallStatusText()
  const formattedDuration = useMemo(() => formatCallDuration(duration), [
    duration,
  ])

  if (state === 'none' || state === 'ongoing' || !opponentId) return null
  return (
    <Root
      // FIX background
      sx={{ background: 'linear-gradient(180deg, #998CD9 0%, #5540BF 100%)' }}
    >
      {opponentImage && <BackgroundImage src={opponentImage} />}
      <BlackOverlay />

      <BackButton onClick={closePvCallManager} sx={{ color: 'neutral000' }}>
        <ArrowRightIcon />
      </BackButton>

      <OpponentInfo>
        <UserAvatar
          disableCompression
          userId={opponentId}
          size={AvatarSize._4XL}
          sx={{ cursor: 'default' }}
        />

        <Typography variant="body1" sx={{ mt: 2, color: 'white' }}>
          {opponentName}
        </Typography>
        <Typography
          variant="body2"
          sx={{ mt: 1, color: 'white', opacity: 0.5 }}
        >
          {statusText}
        </Typography>

        {status === 'successful' && formattedDuration && (
          <CallDuration variant="caption1">{formattedDuration}</CallDuration>
        )}
      </OpponentInfo>

      <div style={{ width: '100%', position: 'absolute', bottom: 48 }}>
        {state === 'outgoing' ? (
          <OutgoingCallControls />
        ) : state === 'incoming' ? (
          <IncomingCallControls />
        ) : state === 'ended' ? (
          <EndedCallControls />
        ) : null}
      </div>
    </Root>
  )
}
